(() => {
  const TEMPLATE_MARKER = '__gallery_section_LNxCGb';
  const ACTIVE_CLASS = 'is-active';
  const ENHANCED_ATTR = 'data-ksc-gallery-enhanced';

  function isKscPdpLayout() {
    return document.body.classList.contains('ksc-pdp-layout') || Boolean(document.querySelector(`[id*="${TEMPLATE_MARKER}"]`));
  }

  function formatIndex(index) {
    return String(index + 1).padStart(2, '0');
  }

  function getGalleryRoot() {
    if (!isKscPdpLayout()) return null;

    const mainProduct = document.querySelector('product-info[id^="MainProduct-"]');
    if (!mainProduct) return null;

    const mediaGallery = mainProduct.querySelector('media-gallery');
    if (!mediaGallery) return null;

    return { mainProduct, mediaGallery };
  }

  function getVisibleImageMediaTargets(mediaGallery) {
    const imageMediaItems = Array.from(mediaGallery.querySelectorAll('.product__media-item[data-media-id]')).filter(
      (item) => item.querySelector('.product__modal-opener--image img')
    );

    return new Set(imageMediaItems.map((item) => item.dataset.mediaId).filter(Boolean));
  }

  function isVisibleElement(element) {
    if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function getThumbnailItems(mediaGallery) {
    const imageMediaTargets = getVisibleImageMediaTargets(mediaGallery);

    return Array.from(mediaGallery.querySelectorAll('.thumbnail-list__item[data-target]')).filter((item) =>
      imageMediaTargets.has(item.dataset.target) && isVisibleElement(item)
    );
  }

  function getPagerButtons(pager) {
    return Array.from(pager.querySelectorAll('.ksc-media-pager__number'));
  }

  function getActiveMedia(mediaGallery) {
    return mediaGallery.querySelector('.product__media-item.is-active');
  }

  function getVisibleGallerySlides(mediaGallery) {
    return Array.from(mediaGallery.querySelectorAll('.product__media-list .product__media-item[data-media-id]')).filter(
      (item) => item.querySelector('.product__modal-opener--image img')
    );
  }

  function getActiveTarget(mediaGallery) {
    const activeThumb = getThumbnailItems(mediaGallery).find((item) => item.querySelector('button[aria-current="true"]'));
    if (activeThumb?.dataset.target) return activeThumb.dataset.target;

    return getActiveMedia(mediaGallery)?.dataset.mediaId || '';
  }

  function syncNativeCounter(mediaGallery) {
    const total = mediaGallery.querySelector('[id^="GalleryViewer-"] .slider-counter--total');
    if (!total) return;

    const visibleCount = getVisibleGallerySlides(mediaGallery).length;
    if (!visibleCount) return;

    const count = String(visibleCount);
    if (total.textContent !== count) {
      total.textContent = count;
    }
  }

  function bindNativeCounterSync(mediaGallery) {
    if (mediaGallery.kscNativeCounterController) {
      mediaGallery.kscNativeCounterController.abort();
      mediaGallery.kscNativeCounterController = null;
    }

    const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
    const slider = viewer?.querySelector('[id^="Slider-Gallery"]');
    const sliderButtons = viewer?.querySelector('.slider-buttons');
    if (!viewer || !slider) return;

    const controller = new AbortController();
    const { signal } = controller;

    let syncFrame;
    const scheduleSync = () => {
      window.cancelAnimationFrame(syncFrame);
      syncFrame = window.requestAnimationFrame(() => syncNativeCounter(mediaGallery));
    };

    viewer.addEventListener('slideChanged', scheduleSync, { signal });
    slider.addEventListener('scroll', scheduleSync, { passive: true, signal });
    sliderButtons?.addEventListener('click', scheduleSync, { signal });

    mediaGallery.kscNativeCounterController = controller;
  }

  function animateActiveMedia(mediaGallery) {
    const activeMedia = getActiveMedia(mediaGallery);
    if (!activeMedia) return;

    const currentTarget = activeMedia.dataset.mediaId || '';
    const previousTarget = mediaGallery.dataset.kscAnimatedTarget || '';
    if (previousTarget === currentTarget) return;

    mediaGallery.dataset.kscAnimatedTarget = currentTarget;

    const animatedElement =
      activeMedia.querySelector('.product-media-container') ||
      activeMedia.querySelector('.deferred-media__poster') ||
      activeMedia.querySelector('.product__media');

    if (!animatedElement || typeof animatedElement.animate !== 'function') return;

    const pagerButtons = getPagerButtons(mediaGallery.querySelector('.ksc-media-pager') || mediaGallery);
    const currentIndex = pagerButtons.findIndex((button) => button.dataset.target === currentTarget);
    const previousIndex = pagerButtons.findIndex((button) => button.dataset.target === previousTarget);
    const movingBackward = previousIndex !== -1 && currentIndex !== -1 && currentIndex < previousIndex;
    const startX = movingBackward ? '-28px' : '28px';

    animatedElement.animate(
      [
        { opacity: 0.45, transform: `translateX(${startX}) scale(0.992)` },
        { opacity: 1, transform: 'translateX(0) scale(1)' },
      ],
      {
        duration: 360,
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }
    );
  }

  function syncPager(mediaGallery, pager) {
    const activeTarget = getActiveTarget(mediaGallery);
    const buttons = getPagerButtons(pager);
    if (!buttons.length) return;

    buttons.forEach((button) => {
      button.classList.toggle(ACTIVE_CLASS, button.dataset.target === activeTarget);
    });

    const activeIndex = buttons.findIndex((button) => button.classList.contains(ACTIVE_CLASS));
    const prev = pager.querySelector('[data-ksc-media-nav="prev"]');
    const next = pager.querySelector('[data-ksc-media-nav="next"]');

    if (prev) prev.disabled = activeIndex <= 0;
    if (next) next.disabled = activeIndex === -1 || activeIndex >= buttons.length - 1;
  }

  function buildPager(mediaGallery) {
    const existingPager = mediaGallery.querySelector('.ksc-media-pager');
    if (existingPager) existingPager.remove();

    const thumbnailItems = getThumbnailItems(mediaGallery);
    if (thumbnailItems.length <= 1) return null;

    const prevIcon = `
      <svg aria-hidden="true" viewBox="0 0 18 18" fill="none">
        <path d="M11.25 3.75 6 9l5.25 5.25" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    const nextIcon = `
      <svg aria-hidden="true" viewBox="0 0 18 18" fill="none">
        <path d="M6.75 3.75 12 9l-5.25 5.25" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    const pager = document.createElement('div');
    pager.className = 'ksc-media-pager';
    pager.innerHTML = `
      <button type="button" class="ksc-media-pager__arrow" data-ksc-media-nav="prev" aria-label="Previous media">
        <span class="svg-wrapper">${prevIcon}</span>
      </button>
      <div class="ksc-media-pager__numbers" role="tablist" aria-label="Product media pagination"></div>
      <button type="button" class="ksc-media-pager__arrow" data-ksc-media-nav="next" aria-label="Next media">
        <span class="svg-wrapper">${nextIcon}</span>
      </button>
    `;

    const numbers = pager.querySelector('.ksc-media-pager__numbers');

    thumbnailItems.forEach((item, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ksc-media-pager__number';
      button.dataset.target = item.dataset.target || '';
      button.textContent = formatIndex(index);
      numbers.appendChild(button);
    });

    mediaGallery.appendChild(pager);
    return pager;
  }

  function bindPagerInteractions(mediaGallery, pager) {
    if (!pager || pager.dataset.kscBound === 'true') return;

    pager.addEventListener('click', (event) => {
      const pagerButton = event.target.closest('.ksc-media-pager__number');
      if (pagerButton) {
        const targetThumb = getThumbnailItems(mediaGallery).find((item) => item.dataset.target === pagerButton.dataset.target);
        targetThumb?.querySelector('button')?.click();
        return;
      }

      const navButton = event.target.closest('[data-ksc-media-nav]');
      if (!navButton || navButton.disabled) return;

      const buttons = getPagerButtons(pager);
      const currentIndex = buttons.findIndex((button) => button.classList.contains(ACTIVE_CLASS));
      const delta = navButton.dataset.kscMediaNav === 'next' ? 1 : -1;
      const targetButton = buttons[currentIndex + delta];
      targetButton?.click();
    });

    pager.dataset.kscBound = 'true';
  }

  function goToAdjacentMedia(mediaGallery, direction) {
    const pager = mediaGallery.querySelector('.ksc-media-pager');
    if (!pager) return;

    const buttons = getPagerButtons(pager);
    const currentIndex = buttons.findIndex((button) => button.classList.contains(ACTIVE_CLASS));
    const targetButton = buttons[currentIndex + direction];
    targetButton?.click();
  }

  function bindSurfaceInteractions(mediaGallery) {
    if (mediaGallery.dataset.kscSurfaceBound === 'true') return;

    const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    let pointerStartX = null;
    let pointerStartY = null;
    let pointerMoved = false;

    viewer.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      pointerMoved = false;
    });

    viewer.addEventListener('pointermove', (event) => {
      if (pointerStartX === null || pointerStartY === null) return;

      const deltaX = event.clientX - pointerStartX;
      const deltaY = event.clientY - pointerStartY;

      if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
        pointerMoved = true;
      }
    });

    viewer.addEventListener(
      'pointerup',
      (event) => {
        if (pointerStartX === null || pointerStartY === null) return;

        const deltaX = event.clientX - pointerStartX;
        const deltaY = event.clientY - pointerStartY;
        pointerStartX = null;
        pointerStartY = null;

        if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.35) {
          event.preventDefault();
          event.stopPropagation();
          goToAdjacentMedia(mediaGallery, deltaX < 0 ? 1 : -1);
          return;
        }

        if (!pointerMoved) {
          const activeMedia = getActiveMedia(mediaGallery);
          if (!activeMedia || !activeMedia.contains(event.target)) return;

          const rect = activeMedia.getBoundingClientRect();
          const clickedRightSide = event.clientX >= rect.left + rect.width / 2;

          event.preventDefault();
          event.stopPropagation();
          goToAdjacentMedia(mediaGallery, clickedRightSide ? 1 : -1);
        }
      },
      true
    );

    viewer.addEventListener('pointercancel', () => {
      pointerStartX = null;
      pointerStartY = null;
      pointerMoved = false;
    });

    mediaGallery.dataset.kscSurfaceBound = 'true';
  }

  function bindGalleryObserver(mediaGallery, pager) {
    if (mediaGallery.kscGalleryObserver) {
      mediaGallery.kscGalleryObserver.disconnect();
      mediaGallery.kscGalleryObserver = null;
    }

    const observer = new MutationObserver(() => {
      syncNativeCounter(mediaGallery);
      syncPager(mediaGallery, mediaGallery.querySelector('.ksc-media-pager') || pager);
      animateActiveMedia(mediaGallery);
    });

    observer.observe(mediaGallery, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-current', 'class'],
    });

    mediaGallery.kscGalleryObserver = observer;
  }

  function enhanceGallery() {
    const refs = getGalleryRoot();
    if (!refs) return;

    const { mediaGallery } = refs;
    document.body.classList.add('ksc-pdp-template');
    bindNativeCounterSync(mediaGallery);

    if (mediaGallery.getAttribute(ENHANCED_ATTR) === 'true') {
      const pager = mediaGallery.querySelector('.ksc-media-pager');
      const expectedCount = getThumbnailItems(mediaGallery).length;

      if (getPagerButtons(pager || mediaGallery).length === expectedCount) {
        syncNativeCounter(mediaGallery);
        syncPager(mediaGallery, pager);
        animateActiveMedia(mediaGallery);
        return;
      }

      pager?.remove();
      mediaGallery.removeAttribute(ENHANCED_ATTR);
    }

    const pager = buildPager(mediaGallery);
    if (!pager) {
      syncNativeCounter(mediaGallery);
      animateActiveMedia(mediaGallery);
      return;
    }

    mediaGallery.setAttribute(ENHANCED_ATTR, 'true');
    mediaGallery.classList.add('ksc-gallery-enhanced');

    bindPagerInteractions(mediaGallery, pager);
    bindSurfaceInteractions(mediaGallery);
    bindGalleryObserver(mediaGallery, pager);
    syncNativeCounter(mediaGallery);
    syncPager(mediaGallery, pager);

    requestAnimationFrame(() => {
      animateActiveMedia(mediaGallery);
    });
  }

  let enhanceTimer;
  function scheduleGalleryEnhance() {
    window.clearTimeout(enhanceTimer);
    enhanceTimer = window.setTimeout(enhanceGallery, 120);
  }

  function teardownGallery() {
    const refs = getGalleryRoot();
    if (!refs) return;

    const { mediaGallery } = refs;
    if (mediaGallery.kscGalleryObserver) {
      mediaGallery.kscGalleryObserver.disconnect();
      mediaGallery.kscGalleryObserver = null;
    }
    if (mediaGallery.kscNativeCounterController) {
      mediaGallery.kscNativeCounterController.abort();
      mediaGallery.kscNativeCounterController = null;
    }
  }

  document.addEventListener('DOMContentLoaded', enhanceGallery);
  document.addEventListener('shopify:section:load', enhanceGallery);
  document.addEventListener('shopify:section:unload', teardownGallery);
  window.addEventListener('load', scheduleGalleryEnhance);
  window.addEventListener('resize', scheduleGalleryEnhance, { passive: true });
  window.addEventListener('orientationchange', scheduleGalleryEnhance);
})();

(() => {
  const TEMPLATE_MARKER = '__gallery_section_LNxCGb';
  const PRODUCT_SELECTOR = 'product-info[id^="MainProduct-"]';
  let kingSupplyPhotoSwipeModulePromise;

  function isKscPdpLayout() {
    return document.body.classList.contains('ksc-pdp-layout') || Boolean(document.querySelector(`[id*="${TEMPLATE_MARKER}"]`));
  }

  function getScriptAssetUrl(fileName) {
    const script =
      document.currentScript ||
      document.querySelector('script[src*="custom-script.js"]') ||
      document.querySelector('script[d-src*="custom-script.js"]');
    const source = script?.src || script?.getAttribute?.('d-src') || '';

    if (!source) return '';

    return source.replace(/custom-script\.js(?:\?.*)?$/, fileName);
  }

  function getPhotoSwipeModule() {
    if (!kingSupplyPhotoSwipeModulePromise) {
      const assetUrl = window.kingSupplyThemeAssets?.photoswipe || getScriptAssetUrl('kingsupply-photoswipe.min.js');
      if (!assetUrl) {
        return Promise.reject(new Error('KingSupply PhotoSwipe asset URL not found.'));
      }

      kingSupplyPhotoSwipeModulePromise = import(assetUrl);
    }

    return kingSupplyPhotoSwipeModulePromise;
  }

  function openFallbackProductModal(toggle) {
    const modalOpener = toggle.closest('modal-opener');
    const modalSelector = modalOpener?.getAttribute('data-modal');
    const modal = modalSelector ? document.querySelector(modalSelector) : null;

    if (modal && typeof modal.show === 'function') {
      modal.show(toggle);
    }
  }

  function isKscPdp() {
    return isKscPdpLayout();
  }

  function getImageSlides(productRoot) {
    return Array.from(productRoot.querySelectorAll('.product__media-list .product__media-item[data-media-id]')).filter(
      (item) => item.querySelector('.product__modal-opener--image img')
    );
  }

  function getDataSource(productRoot) {
    return getImageSlides(productRoot)
      .map((item) => {
        const image = item.querySelector('.product__modal-opener--image img');
        if (!image) return null;

        const width = parseInt(image.getAttribute('width'), 10) || image.naturalWidth || 1600;
        const height = parseInt(image.getAttribute('height'), 10) || image.naturalHeight || 2000;

        return {
          src: image.currentSrc || image.src,
          srcset: image.getAttribute('srcset') || '',
          msrc: image.currentSrc || image.src,
          alt: image.getAttribute('alt') || '',
          width,
          height,
        };
      })
      .filter(Boolean);
  }

  function registerKingSupplyLightboxUi(lightbox, totalItems) {
    lightbox.on('uiRegister', () => {
      if (totalItems > 1) {
        lightbox.ui.registerElement({
          name: 'kingsupply-prev',
          order: 1,
          isButton: true,
          appendTo: 'bar',
          className: 'kingsupply-lightbox__button kingsupply-lightbox__button--arrow',
          ariaLabel: 'Previous image',
          html: `
            <svg aria-hidden="true" viewBox="0 0 16 18" fill="none">
              <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square" stroke-width="1.5"/>
            </svg>
          `,
          onClick: () => lightbox.prev(),
        });

        lightbox.ui.registerElement({
          name: 'kingsupply-counter',
          order: 2,
          appendTo: 'bar',
          className: 'kingsupply-lightbox__counter',
          html: `1 / ${totalItems}`,
          onInit: (element, instance) => {
            const renderCounter = () => {
              element.textContent = `${instance.currIndex + 1} / ${totalItems}`;
            };

            renderCounter();
            instance.on('change', renderCounter);
          },
        });
      }

      lightbox.ui.registerElement({
        name: 'kingsupply-close',
        order: 3,
        isButton: true,
        appendTo: 'bar',
        className: 'kingsupply-lightbox__button kingsupply-lightbox__button--close',
        ariaLabel: 'Close gallery',
        html: `
          <svg aria-hidden="true" viewBox="0 0 18 17" fill="none">
            <path d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z" fill="currentColor"/>
          </svg>
        `,
        onClick: (event) => {
          event?.preventDefault?.();
          event?.stopPropagation?.();
          lightbox.close();
        },
      });

      if (totalItems > 1) {
        lightbox.ui.registerElement({
          name: 'kingsupply-next',
          order: 4,
          isButton: true,
          appendTo: 'bar',
          className: 'kingsupply-lightbox__button kingsupply-lightbox__button--arrow',
          ariaLabel: 'Next image',
          html: `
            <svg aria-hidden="true" viewBox="0 0 16 18" fill="none">
              <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square" stroke-width="1.5"/>
            </svg>
          `,
          onClick: () => lightbox.next(),
        });
      }
    });
  }

  async function openKingSupplyLightbox(productRoot, clickedSlide) {
    const dataSource = getDataSource(productRoot);
    if (!dataSource.length) return;

    const imageSlides = getImageSlides(productRoot);
    const startIndex = Math.max(imageSlides.indexOf(clickedSlide), 0);
    const { default: PhotoSwipe } = await getPhotoSwipeModule();

    const lightbox = new PhotoSwipe({
      dataSource,
      index: startIndex,
      mainClass: 'kingsupply-lightbox',
      bgOpacity: 1,
      showHideAnimationType: 'zoom',
      loop: dataSource.length > 1,
      arrowKeys: true,
      closeOnVerticalDrag: true,
      pinchToClose: true,
      secondaryZoomLevel: 2.5,
      maxZoomLevel: 4,
      spacing: 0.1,
      arrowPrev: false,
      arrowNext: false,
      bgClickAction: 'close',
      tapAction: 'close',
      close: false,
      counter: false,
      zoom: false,
    });

    registerKingSupplyLightboxUi(lightbox, dataSource.length);
    lightbox.init();
  }

  function bindKingSupplyLightbox() {
    if (document.documentElement.dataset.kingsupplyLightboxBound === 'true') return;

    document.documentElement.dataset.kingsupplyLightboxBound = 'true';
    window.kingSupplyLightboxReady = true;

    document.addEventListener(
      'click',
      async (event) => {
        if (!isKscPdp()) return;

        const toggle = event.target.closest('.product__media-item .product__modal-opener--image .product__media-toggle');
        if (!toggle) return;

        const productRoot = toggle.closest(PRODUCT_SELECTOR);
        if (!productRoot) return;

        const mediaGallery = toggle.closest('media-gallery');
        const galleryViewer = mediaGallery?.querySelector('[id^="GalleryViewer"]');

        if (galleryViewer?.contains(toggle)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const clickedSlide = toggle.closest('.product__media-item');
        if (!clickedSlide) return;

        try {
          await openKingSupplyLightbox(productRoot, clickedSlide);
        } catch {
          openFallbackProductModal(toggle);
        }
      },
      true
    );
  }

  function initKingSupplyLightbox() {
    bindKingSupplyLightbox();
  }

  initKingSupplyLightbox();
  document.addEventListener('DOMContentLoaded', initKingSupplyLightbox);
  document.addEventListener('shopify:section:load', initKingSupplyLightbox);
  document.addEventListener('product-info:loaded', initKingSupplyLightbox);
})();

(() => {
  const PRICE_SELECTOR = '.ksc-gallery-insert-overlay__price';

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
    const sectionId = data?.sectionId;
    const html = data?.html;
    if (!sectionId || !html) return;

    const productInfo = document.querySelector(`product-info[data-section="${sectionId}"]`);
    const currentPrice = productInfo?.querySelector(PRICE_SELECTOR);
    const updatedPrice = html.querySelector(PRICE_SELECTOR);
    if (!currentPrice || !updatedPrice) return;

    currentPrice.textContent = updatedPrice.textContent;
  });
})();
